import { Link } from '@tanstack/react-router';

import { Button } from '@/components/button';
import { AppImage } from '@/components/layout/app-image';
import { Icons } from '@/components/icons';
import type { PublicProjectRecord } from '@/content/schemas';

type ContactCtaProps = {
  label?: string;
  className?: string;
};

export function ContactCta({
  label = "Let's collaborate",
  className,
}: ContactCtaProps) {
  return (
    <Button variant="brand" size="pill" asChild className={className}>
      <Link to="/contact">
        {label}
        <span className="bg-primary-foreground/20 ml-2 flex size-6 items-center justify-center rounded-full">
          <Icons.arrowRight className="size-3" />
        </span>
      </Link>
    </Button>
  );
}

type FeaturedProjectCardProps = {
  project: PublicProjectRecord;
  index: number;
};

export function FeaturedProjectCard({
  project,
  index,
}: FeaturedProjectCardProps) {
  if (!project.route) {
    return null;
  }

  return (
    <article className="border-border grid gap-8 overflow-hidden rounded-2xl border md:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)]">
      <div className="bg-muted relative aspect-[16/10] md:aspect-auto md:min-h-[280px]">
        <AppImage
          src={project.hero.src}
          alt={project.hero.alt}
          fill
          className="object-cover"
        />
      </div>

      <div className="flex flex-col gap-4 p-6 md:p-8">
        <p className="text-muted-foreground font-mono text-xs uppercase tracking-widest">
          Selected work {String(index).padStart(2, '0')}
        </p>
        <h2 className="font-display text-ink text-2xl font-semibold leading-snug md:text-3xl">
          <Link
            to={project.route}
            className="hover:text-brand transition-colors"
          >
            {project.title}
          </Link>
        </h2>
        <p className="text-muted-foreground text-sm leading-relaxed md:text-base">
          {project.description}
        </p>
        {project.role && (
          <p className="text-muted-foreground font-mono text-xs">
            Role: {project.role}
          </p>
        )}
        {project.outcomes && project.outcomes.length > 0 && (
          <dl className="border-border grid gap-3 border-y py-4 sm:grid-cols-2">
            {project.outcomes.map((outcome) => (
              <div key={`${outcome.label}-${outcome.value}`}>
                <dt className="text-muted-foreground font-mono text-[10px] uppercase tracking-widest">
                  {outcome.label}
                </dt>
                <dd className="font-display text-ink text-sm font-semibold">
                  {outcome.value}
                </dd>
              </div>
            ))}
          </dl>
        )}
        <div className="mt-auto">
          <Link
            to={project.route}
            className="text-brand font-mono text-sm font-medium hover:underline"
          >
            Read case study for {project.title}
          </Link>
        </div>
      </div>
    </article>
  );
}
